<template>
  <div class="card">
    <div class="pb-0 card-header mb-0 d-flex align-items-center justify-content-between flex-wrap gap-2">
      <div>
        <h6 class="section-title chart-card-title mb-0">{{ title }}</h6>
        <p class="text-sm mb-0 mt-1">
          <span class="font-weight-bold">{{ detail1 }}</span>
          {{ detail2 }}
        </p>
      </div>
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <div class="chart-filter">
          <label class="form-label text-sm mb-0 me-1">Buscar</label>
          <input
            v-model="productSearch"
            type="text"
            class="form-control form-control-sm"
            style="width: auto; min-width: 180px;"
            placeholder="Buscar producto..."
            @input="onProductSearchInput"
          />
        </div>
        <div class="chart-filter">
          <label class="form-label text-sm mb-0 me-1">Mes</label>
          <select v-model="selectedMonth" class="form-select form-select-sm" style="width: auto; min-width: 120px;" @change="fetchYearAnalytics">
            <option v-for="(name, key) in monthOptions" :key="key" :value="key">{{ name }}</option>
          </select>
        </div>
      </div>
    </div>
    <div class="p-3 card-body">
      <div class="chart">
        <canvas ref="chartLineCanvas" class="chart-canvas" height="300"></canvas>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js/auto";
import { mapActions } from 'vuex';

export default {
  name: "gradient-line-chart",
  data() {
    const now = new Date();
    const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
    const monthNames = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
    const monthOptions = {};
    monthNames.forEach((name, i) => { monthOptions[String(i + 1).padStart(2, '0')] = name; });
    return {
      data: {},
      chartInstance: null,
      selectedMonth: currentMonth,
      productSearch: "",
      monthOptions,
      productSearchTimer: null,
    }
  },
  props: {
    title: {
      type: String,
      default: "Producción terminadas por mes",
    },
    detail1: {
      type: String,
      default: "",
    },
    detail2: {
      type: String,
      default: "",
    },
  },
  methods: {
    ...mapActions('analitycs', ['getYear']),
    onProductSearchInput() {
      if (this.productSearchTimer) clearTimeout(this.productSearchTimer);
      this.productSearchTimer = setTimeout(() => this.fetchYearAnalytics(), 300);
    },
    async fetchYearAnalytics() {
      try {
        const params = { month: this.selectedMonth };
        const q = (this.productSearch || "").trim();
        if (q) params.product_search = q;
        const grafico = await this.getYear(params);
        this.data = grafico?.data || {};
      } catch (error) {
        this.data = {};
      } finally {
        this.updateChart();
      }
    },
    updateChart() {
      const key = this.selectedMonth;
      const data = this.data[key] || {
        month: this.monthOptions[key] || key,
        primera: 0,
        segunda: 0,
        tercera: 0,
        averia_cargue: 0,
        averia_secado: 0,
      };

      const labels = ['Primera', 'Segunda', 'Tercera', 'Avería de cargue', 'Avería de secado'];
      const values = [
        data.primera ?? 0,
        data.segunda ?? 0,
        data.tercera ?? 0,
        data.averia_cargue ?? 0,
        data.averia_secado ?? 0,
      ];
      const colors = [
        'rgba(31, 45, 90, 0.85)',
        'rgba(246, 165, 0, 0.85)',
        'rgba(94, 114, 228, 0.85)',
        'rgba(136, 152, 170, 0.85)',
        'rgba(45, 206, 137, 0.85)',
      ];
      const borders = ['#1f2d5a', '#f6a500', '#5e72e4', '#8898aa', '#2dce89'];

      const canvas = this.$refs.chartLineCanvas;
      if (!canvas || typeof canvas.getContext !== "function") return;
      const ctx1 = canvas.getContext("2d");
      if (!ctx1) return;
      const existingChart = Chart.getChart(canvas);
      if (existingChart) existingChart.destroy();
      if (this.chartInstance) this.chartInstance.destroy();

      this.chartInstance = new Chart(ctx1, {
        type: "bar",
        data: {
          labels,
          datasets: [{
            label: data.month,
            data: values,
            backgroundColor: colors,
            borderColor: borders,
            borderWidth: 1,
            barThickness: 50,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => `${ctx.label}: ${ctx.parsed.y}`,
                footer: (items) => "Total: " + items.reduce((s, i) => s + (i.parsed.y || 0), 0),
              },
            },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { color: "#67748e", padding: 10, font: { size: 11 } },
              barPercentage: 1,
              categoryPercentage: 1,
            },
            y: {
              beginAtZero: true,
              grid: { drawBorder: false, borderDash: [5, 5], color: "rgba(0,0,0,0.06)" },
              ticks: { color: "#67748e", padding: 10, font: { size: 11 } },
            },
          },
        },
      });
    }
  },
  mounted() {
    this.fetchYearAnalytics();
  },
  beforeUnmount() {
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }
    if (this.productSearchTimer) clearTimeout(this.productSearchTimer);
  },
};
</script>

<style scoped>
.chart-card-title.section-title {
  font-weight: 800;
  color: #1f2d5a;
  letter-spacing: 0.6px;
}

.chart-card-title.section-title::after {
  content: "";
  display: block;
  width: 44px;
  height: 3px;
  margin-top: 6px;
  border-radius: 999px;
  background: #f6a500;
}

.chart-filter {
  display: inline-flex;
  align-items: center;
}
</style>